import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Typography, List, Switch, Menu, Collapse, Button, Tooltip, Spin, Checkbox, Empty } from "antd";
import { LayoutFilled, SaveFilled, InfoCircleFilled, WarningFilled, ProductFilled } from '@ant-design/icons';
import { createAssignments } from "../../../../API/assignments";
import AssetAssignmentTable from "./components/AssetsAsignmentTable/AssetsAsignmentTable";
import ControlsFilter from "../AssignmentControls/components/ControlsFilter/ControlsFilter";

const { Panel } = Collapse;
const { Title } = Typography;

const AssignmentAssets = ({
  assets = [],
  loading,
  selectedAsset,
  selectedControl,
  assignmentsDependenciesToControls = {},
  assignmentDependenciesHistory = [],
  setSelectedAsset,
  setSelectedControl,
  setAssignmentsDependenciesToControls,
  setAssignmentDependenciesHistory,
  priorities,
  types,
  api,
  initialSearchTerm,
  registerSaveFunction,
  permits
}) => {
  const [selectAll, setSelectAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm || '');
  const [filters, setFilters] = useState({
    priority: null,
    type: null,
    enabled: null
  });

  // Efecto para manejar la selección inicial cuando hay término de búsqueda
  useEffect(() => {
    if (initialSearchTerm && assets.length > 0) {
      // Buscar el control que coincide con el término de búsqueda
      const matchingControl = assets.flatMap(asset => 
        asset.controls?.filter(control => 
          control.title.toLowerCase().includes(initialSearchTerm.toLowerCase())
        ) || []
      )[0];
      
      if (matchingControl) {
        onControlSelect(matchingControl.key);
      }
    }
  }, [initialSearchTerm, assets]);

  // Función segura para obtener activos del control seleccionado
  const getCurrentAssets = () => {
    if (!selectedControl) return [];
    return assets.filter(asset => 
      asset.controls?.some(control => control.key === selectedControl.key)
    );
  };

  // Función segura para obtener dependencias del activo seleccionado
  const getCurrentDependencies = () => {
    if (!selectedAsset || !selectedControl) return [];
    const asset = assets.find(a => a.key === selectedAsset.key);
    const control = asset?.controls?.find(c => c.key === selectedControl.key);
    return control?.dependencies || [];
  };

  const getDependencyFlag = (dependencyCode) => {
    if (!selectedAsset || !selectedControl) return false;
    return assignmentsDependenciesToControls[selectedAsset.key]?.[selectedControl.key]?.[dependencyCode] ?? false;
  };

  const onControlSelect = (controlKey) => {
    const selected = assets.flatMap(asset => 
      asset.controls?.filter(control => control.key === controlKey) || []
    )[0];
    
    if (selected) {
      setSelectedControl({
        ...selected,
        dependencies: selected.dependencies || []
      });
      setSelectedAsset(null);
      setSelectAll(false);
    }
  };

  const onAssetSelect = (assetKey) => {
    if (!selectedControl) return;

    const selected = assets.find(asset => asset.key === assetKey);
    
    if (selected) {
      setSelectedAsset({
        ...selected,
        controls: selected.controls?.map(control => ({
          ...control,
          dependencies: control.dependencies || []
        })) || []
      });
      setSelectAll(false);
    }
  };

  const onSelectAllChange = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);

    if (!selectedAsset || !selectedControl) return;

    setAssignmentsDependenciesToControls(prevAssignments => {
      const updatedAssignments = {
        ...prevAssignments,
        [selectedAsset.key]: {
          ...prevAssignments[selectedAsset.key],
          [selectedControl.key]: getCurrentDependencies().reduce((acc, dep) => {
            acc[dep.code] = checked;
            return acc;
          }, {}),
        },
      };

      const updatedHistory = getCurrentDependencies().map(dep => ({
        asset_code: selectedAsset.key,
        control_code: selectedControl.key,
        dependency_code: dep.code,
        is_active: checked,
      }));

      setAssignmentDependenciesHistory(updatedHistory);

      return updatedAssignments;
    });
  };

  const onDependencySwitchChange = (checked, dependencyCode) => {
    if (!selectedAsset || !selectedControl) return;

    setAssignmentsDependenciesToControls(prevAssignments => {
      const updatedAssignments = {
        ...prevAssignments,
        [selectedAsset.key]: {
          ...prevAssignments[selectedAsset.key],
          [selectedControl.key]: {
            ...prevAssignments[selectedAsset.key]?.[selectedControl.key],
            [dependencyCode]: checked,
          },
        },
      };

      const existingEntryIndex = assignmentDependenciesHistory.findIndex(
        entry =>
          entry.asset_code === selectedAsset.key &&
          entry.control_code === selectedControl.key &&
          entry.dependency_code === dependencyCode
      );

      let updatedHistory;

      if (existingEntryIndex >= 0) {
        updatedHistory = assignmentDependenciesHistory.map((entry, index) =>
          index === existingEntryIndex
            ? { ...entry, is_active: checked }
            : entry
        );
      } else {
        updatedHistory = [
          ...assignmentDependenciesHistory,
          {
            asset_code: selectedAsset.key,
            control_code: selectedControl.key,
            dependency_code: dependencyCode,
            is_active: checked,
          },
        ];
      }

      setAssignmentDependenciesHistory(updatedHistory);

      return updatedAssignments;
    });

    // Actualizar selectAll si es necesario
    const allChecked = Object.values(assignmentsDependenciesToControls[selectedAsset.key]?.[selectedControl.key] || {})
      .every(val => val === true);
    setSelectAll(allChecked);
  };

  const onSaveAssignments = useCallback(() => {
    let allChangedDependencies = [];
  
    // Recorrer todos los activos
    Object.keys(assignmentsDependenciesToControls).forEach(assetKey => {
      const asset = assets.find(a => a.key === assetKey);
      if (!asset) return;
  
      // Recorrer todos los controles del activo
      Object.keys(assignmentsDependenciesToControls[assetKey]).forEach(controlKey => {
        const control = asset.controls?.find(c => c.key === controlKey);
        if (!control) return;
  
        const currentDependencies = assignmentsDependenciesToControls[assetKey][controlKey] || {};
        
        // Obtener los valores iniciales
        const initialDependencies = (control.dependencies || []).reduce((acc, dep) => {
          acc[dep.code] = dep.flag;
          return acc;
        }, {});
  
        // Filtrar las dependencias modificadas
        const changedDependencies = Object.keys(currentDependencies)
          .filter(dependencyCode => currentDependencies[dependencyCode] !== initialDependencies[dependencyCode])
          .map(dependencyCode => ({
            asset_code: assetKey,
            control_code: controlKey,
            dependency_code: dependencyCode,
            flag: currentDependencies[dependencyCode],
          }));
  
        allChangedDependencies = [...allChangedDependencies, ...changedDependencies];
      });
    });
  
    if (allChangedDependencies.length === 0) {
      api.info({ 
        message: "No se han realizado cambios en las asignaciones",
        key: 'change-info'
      });
      return;
    }
  
    createAssignments(allChangedDependencies)
      .then(() => {
        api.success({ 
          message: "Asignaciones guardadas con éxito",
          description: `Se ${allChangedDependencies.length === 1 ? 'actualizó' : 'actualizaron'} ${allChangedDependencies.length} control${allChangedDependencies.length === 1 ? '' : 'es'}`
        });        
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      })
      .catch((error) => {
        api.error({ 
          message: "Error al guardar asignaciones",
          description: error.message 
        });
        console.error("Error en createAssignments:", error);
      });
  }, [assignmentsDependenciesToControls, assets, api]);

  // Obtener todos los controles únicos de todos los activos
  const getAllControls = () => {
    const allControls = [];
    const seenControls = new Set();

    assets.forEach(asset => {
      asset.controls?.forEach(control => {
        if (!seenControls.has(control.key)) {
          seenControls.add(control.key);
          
          // Aplicar filtros
          const matchesSearch = control.title.toLowerCase().includes(searchTerm.toLowerCase());
          const matchesPriority = !filters.priority || control.priority?.name === filters.priority;
          const matchesType = !filters.type || control.type?.name === filters.type;
          const matchesEnabled = filters.enabled === null || control.flag === filters.enabled;
          
          if (matchesSearch && matchesPriority && matchesType && matchesEnabled) {
            allControls.push(control);
          }
        }
      });
    });

    return allControls;
  };

  useEffect(() => {
    if (registerSaveFunction) {
      registerSaveFunction(onSaveAssignments);
    }
  }, [onSaveAssignments]);

  return (
    <>
      <Row gutter={16} className="assignment-form" style={{ height: 400 }}>
        <Col span={13}>
          <Row>
            <Col span={4}>
              <Title level={4}>Controles</Title>
            </Col>
            <Col span={20} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ControlsFilter
                searchTerm={searchTerm}
                filters={filters}
                onSearchChange={(e) => setSearchTerm(e.target.value)}
                onFiltersChange={setFilters}
                onSelectAllChange={onSelectAllChange}
                notShowFields={true}
                priorities={priorities}
                types={types}
              />
            </Col>
          </Row>
          <Spin className="custom-spin" spinning={loading}>
          {!loading && getAllControls().length === 0 ? (
            <Empty 
              description="No hay controles disponibles o no se definieron los activos con sus dependencias"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <div className="scrollable-list-container">
              <List
                className="custom-assignment-list"
                dataSource={getAllControls()}
                renderItem={(control) => {
                  const priorityColor = control.priority?.name === 'Alta' ? '#ff4d4f' :
                                      control.priority?.name === 'Media' ? '#faad14' :
                                      control.priority?.name === 'Baja' ? '#52c41a' : '#006939';

                  return (
                    <List.Item
                      onClick={() => onControlSelect(control.key)}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: selectedControl?.key === control.key ? '#f0f0f0' : 'transparent',
                        padding: '8px'
                      }}
                    >
                      <List.Item.Meta
                        title={
                          <Collapse
                            defaultActiveKey={[]}
                            expandIconPosition="end"
                            style={{ border: "1px solid #d9d9d9", borderRadius: 8 }}
                          >
                            <Panel
                              key={control.key}
                              header={
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  {/* Icono de Tipo */}
                                  <Tooltip title={`Tipo: ${control.type?.name || "No especificado"}`}>
                                    <ProductFilled style={{ 
                                      fontSize: 18, 
                                      color: "#006939", 
                                      marginRight: 8,
                                      visibility: control.type ? 'visible' : 'hidden'
                                    }} />
                                  </Tooltip>
                                  
                                  {/* Icono de Prioridad */}
                                  <Tooltip title={`Prioridad: ${control.priority?.name || "No especificado"}`}>
                                    <WarningFilled style={{ 
                                      fontSize: 18, 
                                      marginRight: 8,
                                      color: priorityColor,
                                      visibility: control.priority ? 'visible' : 'hidden'
                                    }} />
                                  </Tooltip>
                                  
                                  {/* Título del Control */}
                                  <Title level={5} style={{ margin: 0 }}>
                                    {control.title}
                                  </Title>
                                </div>
                              }
                            >
                              <div style={{ padding: '8px 0' }}>
                                {control.description || "Descripción no disponible"}
                              </div>
                            </Panel>
                          </Collapse>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
              </div>
            )}
</Spin>
            
          </Col>
          <Col span={5}>
            <Title level={4}>Activos</Title>
            {selectedControl ? (
              <div className="scrollable-list-container">
                <Menu 
                  className="custom-assignment-menu" 
                  onClick={({ key }) => onAssetSelect(key)} 
                  mode="inline"
                  selectedKeys={selectedAsset ? [selectedAsset.key] : []}
                >
                  {getCurrentAssets().map((asset) => (
                    <Menu.Item key={asset.key} icon={<LayoutFilled />}>
                      {asset.title}
                    </Menu.Item>
                  ))}
                </Menu>
              </div>
            ) : (
              <p>Seleccione un control para ver los activos.</p>
            )}
          </Col>
          <Col span={6}>
            <Row>
              <Col span={18}>
                <Title level={4}>Dependencias</Title>
              </Col>
            </Row>
            {selectedAsset && selectedControl ? (
              <div className="scrollable-list-container">
                {/* <List
                  className="custom-assignment-list"
                  dataSource={getCurrentDependencies()}
                  renderItem={(dependency) => (
                    <List.Item
                      actions={[
                        <Switch
                          className="custom-assignment-switch"
                          key={dependency.code}
                          checked={getDependencyFlag(dependency.code)}
                          onChange={(checked) => onDependencySwitchChange(checked, dependency.code)}
                        />
                      ]}
                    >
                      <Collapse defaultActiveKey={[]} ghost>
                        <Panel 
                          header={
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <Tooltip title={dependency.description}>
                                <InfoCircleFilled style={{ 
                                  fontSize: 16, 
                                  color: "#006939", 
                                  marginRight: 8 
                                }} />
                              </Tooltip>
                              <span style={{ fontWeight: 500 }}>{dependency.name}</span>
                            </div>
                          }
                          key={dependency.code}
                        >
                          <div style={{ paddingLeft: 24 }}>
                            <p>{dependency.description}</p>
                          </div>
                        </Panel>
                      </Collapse>
                    </List.Item>
                  )}
                /> */}
                <List
                  className="custom-assignment-list"
                  dataSource={getCurrentDependencies()}
                  renderItem={(dependency) => (
                    <List.Item
                      actions={[
                        <Switch
                          className="custom-assignment-switch"
                          disabled={
                            getDependencyFlag(dependency.code) !== dependency.flag
                              ? (getDependencyFlag(dependency.code) ? !dependency.can_assign : !dependency.can_unassign)
                              : (dependency.flag ? !dependency.can_unassign : !dependency.can_assign)
                          }
                          key={dependency.code}
                          checked={getDependencyFlag(dependency.code)}
                          onChange={(checked) => onDependencySwitchChange(checked, dependency.code)}
                        />
                      ]}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <LayoutFilled style={{  color: "#006939", marginRight: 8  }} />
                        <span style={{ fontWeight: 500 }}>{dependency.name}</span>
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            ) : (
              <p>Seleccione un activo para ver las dependencias.</p>
            )}
          </Col>
        </Row>

        <Row style={{ marginTop: 10 }}>
          <Col span={24}>
            <AssetAssignmentTable 
              assignmentsDependenciesToControls={assignmentsDependenciesToControls}
              assets={assets}
            />
          </Col>
          <Col span={24} style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              icon={<SaveFilled />}
              className="save-type-button"
              type="primary"
              onClick={onSaveAssignments}
              disabled={
                assets.length === 0 ||
                !(
                  permits.isSuperuser ||
                  (permits.hasAnyRole && permits.canAssignmentCreate || permits.canAssignmentUpdate)
                )
              }
            >
              Guardar
            </Button>
          </Col>
        </Row>
    </>
  );
};

export default AssignmentAssets;